import { TypeChecker } from '@attain-apps/type-guards'

import {
  ADMIN_ACTIONS,
  ADMIN_TOOL_ACTIONS,
  AGENT_ACTIONS,
  CLIENT_ACTIONS,
  NAVIGATION_ACTIONS,
  POLICY_ACTIONS,
} from './action-groups'
import type { Pattern as PolicyPattern } from './pattern'
import { ResourceType } from './resource-type'

/**
 * Every action group, one per use case in `action-groups/`. Add a new group
 * here; {@link ACTION_REGISTRY} is built from this list
 */
const ACTION_GROUPS = [
  POLICY_ACTIONS,
  NAVIGATION_ACTIONS,
  CLIENT_ACTIONS,
  ADMIN_ACTIONS,
  ADMIN_TOOL_ACTIONS,
  AGENT_ACTIONS,
] as const

/** The groups merged into one object type */
type Merged<TGroups extends readonly object[]> = TGroups extends readonly [
  infer THead,
  ...infer TRest extends readonly object[],
]
  ? THead & Merged<TRest>
  : unknown

/** Keys of each member of a union, rather than only the keys all share */
type KeysOfEach<T> = T extends unknown ? keyof T : never

/** Every action defined in more than one of the given groups */
type DuplicateActions<TGroups extends readonly object[]> =
  TGroups extends readonly [
    infer THead,
    ...infer TRest extends readonly object[],
  ]
    ? (keyof THead & KeysOfEach<TRest[number]>) | DuplicateActions<TRest>
    : never

/**
 * Fails to compile if two groups define the same action, which merging would
 * otherwise resolve silently. The error names the duplicated action
 */
const NO_DUPLICATE_ACTIONS: [DuplicateActions<typeof ACTION_GROUPS>] extends [
  never,
]
  ? true
  : DuplicateActions<typeof ACTION_GROUPS> = true

/**
 * The closed vocabulary of actions, `service:resource:verb`.
 *
 * **Names are frozen once shipped:** stored policies reference them, so
 * renaming one silently breaks every policy that names it.
 *
 * Defined per use case in `action-groups/`; add a new group there and list it
 * in {@link ACTION_GROUPS}.
 */
export const ACTION_REGISTRY = Object.assign({}, ...ACTION_GROUPS) as Merged<
  typeof ACTION_GROUPS
>

/**
 * A registered action, `service:resource:verb`. A literal union derived from
 * {@link ACTION_REGISTRY}, so an unregistered name is a compile error.
 */
export type Action = keyof typeof ACTION_REGISTRY
export namespace Action {
  /** Describes one entry of {@link ACTION_REGISTRY} */
  export interface Definition {
    readonly description: string
    /** The resource types this action is meaningfully granted on */
    readonly resourceTypes: readonly ResourceType[]
  }

  /**
   * What a statement may list: an {@link Action}, a trailing whole-segment
   * wildcard (`admin:*`, `admin:zone:*`), or `*`. `admin:zone*` is invalid.
   * Enforced by validation, not the type.
   */
  export type Pattern =
    | typeof PolicyPattern.WILDCARD
    | Action
    | `${string}${typeof PolicyPattern.TRAILING_WILDCARD}`

  export const ALL: readonly Action[] = Object.keys(ACTION_REGISTRY) as Action[]
  export const isAction = TypeChecker.Factory.oneOf(ALL)

  /** The registry entry for the given {@link Action} */
  export function getDefinition(action: Action): Definition {
    return ACTION_REGISTRY[action]
  }

  /**
   * The action that puts a resource of each type in the user's nav (`me`).
   * Types missing here never appear in it
   */
  export const ACCESS_BY_RESOURCE_TYPE = {
    [ResourceType.Tenant]: 'client:tenant:access',
    [ResourceType.Zone]: 'client:zone:access',
    [ResourceType.App]: 'client:app:access',
    [ResourceType.Scenario]: 'client:scenario:access',
    [ResourceType.Page]: 'client:page:access',
    [ResourceType.Agent]: 'client:agent:access',
  } as const satisfies Partial<Record<ResourceType, Action>>
}
